import {
  DuctType,
  FilterKind,
  type Coil,
  type Damper,
  type Fan,
  type Filter,
  type Flow,
  type FullAirHandlerUnit,
  type Humidifier,
  type ModuleValve,
  type Pumps,
  type RecoveryWheel,
} from "@eisox/api/core-api";
import {
  AirHandlerUnitDefectType,
  AirHandlerUnitObjectType,
  translateDefect,
  type TranslateDefect,
} from "@eisox/building-management-system";

import { getLocale } from "~/paraglide/runtime";
import type { AirHandlerUnitError } from "~/types/air-handler-unit.type";

const getMessage = (props: Omit<TranslateDefect, "language">) =>
  translateDefect({ ...(props as TranslateDefect), language: getLocale() });

const getFanErrors = (fan: Fan): string[] => {
  const errors: string[] = [];

  const fanType = fan.type.toUpperCase() as "SUPPLY" | "RETURN";

  const getFanErrorMessage = (defectType: AirHandlerUnitDefectType) =>
    getMessage({ objectType: AirHandlerUnitObjectType[`${fanType}_FAN`], defectType });

  if (fan.defect) {
    errors.push(getFanErrorMessage(AirHandlerUnitDefectType.DEFECT));
  }

  if (fan.pressureSwitchDefect) {
    errors.push(getFanErrorMessage(AirHandlerUnitDefectType.PRESSURE_SWITCH_DEFECT));
  }

  if (fan.missmatchDefect) {
    errors.push(getFanErrorMessage(AirHandlerUnitDefectType.MISS_MATCH_DEFECT));
  }

  return errors;
};

const getFansErrors = (fans: Fan[]): string[] => {
  const errors: string[] = [];

  fans.forEach(fan => {
    errors.push(...getFanErrors(fan));
  });

  return errors;
};

const getRecoveryWheelErrors = (recoveryWheel: RecoveryWheel): string[] => {
  const errors: string[] = [];

  const getRecoveryWheelErrorMessage = (defectType: AirHandlerUnitDefectType) =>
    getMessage({ objectType: AirHandlerUnitObjectType.RECOVERY_WHEEL, defectType });

  if (recoveryWheel.defect) {
    errors.push(getRecoveryWheelErrorMessage(AirHandlerUnitDefectType.DEFECT));
  }

  if (recoveryWheel.missmatchDefect) {
    errors.push(getRecoveryWheelErrorMessage(AirHandlerUnitDefectType.MISS_MATCH_DEFECT));
  }

  return errors;
};

const getDamperErrors = (damper: Damper): string[] => {
  const errors: string[] = [];

  const damperType = damper.type?.toUpperCase() as "SUPPLY" | "RETURN";

  const getDamperErrorMessage = (defectType: AirHandlerUnitDefectType) =>
    getMessage({ objectType: AirHandlerUnitObjectType[`${damperType}_AIR_DAMPER`], defectType });

  if (damper.defect) {
    errors.push(getDamperErrorMessage(AirHandlerUnitDefectType.DEFECT));
  }

  if (damper.missmatchDefect) {
    errors.push(getDamperErrorMessage(AirHandlerUnitDefectType.MISS_MATCH_DEFECT));
  }

  return errors;
};

const getDampersErrors = (dampers: Damper[]): string[] => {
  const errors: string[] = [];

  dampers.forEach(damper => {
    errors.push(...getDamperErrors(damper));
  });

  return errors;
};

const getTwoWayValveErrors = (twoWayValve: ModuleValve): string[] => {
  const errors: string[] = [];

  const getTwoWayValveErrorMessage = (defectType: AirHandlerUnitDefectType) =>
    getMessage({ objectType: AirHandlerUnitObjectType.COIL_TWO_WAYS_VALVE, defectType });

  if (twoWayValve.defect) {
    errors.push(getTwoWayValveErrorMessage(AirHandlerUnitDefectType.DEFECT));
  }

  return errors;
};

const getThreeWayValveErrors = (threeWayValve: ModuleValve): string[] => {
  const errors: string[] = [];

  const getThreeWayValveErrorMessage = (defectType: AirHandlerUnitDefectType) =>
    getMessage({ objectType: AirHandlerUnitObjectType.COIL_THREE_WAYS_VALVE, defectType });

  if (threeWayValve.defect) {
    errors.push(getThreeWayValveErrorMessage(AirHandlerUnitDefectType.DEFECT));
  }

  return errors;
};

const getPumpErrors = (pump: Pumps): string[] => {
  const errors: string[] = [];

  const getPumpErrorMessage = (defectType: AirHandlerUnitDefectType) =>
    getMessage({ objectType: AirHandlerUnitObjectType.COIL_PUMP, defectType });

  if (pump.defectP1 || pump.defectP2 || pump.missmatchDefectP1 || pump.missmatchDefectP2) {
    errors.push(getPumpErrorMessage(AirHandlerUnitDefectType.DEFECT));
  }

  return errors;
};

const getCoilErrors = (coil: Coil): string[] => {
  const errors: string[] = [];

  const getCoilErrorMessage = (defectType: AirHandlerUnitDefectType) =>
    getMessage({ coilType: coil.coilType, objectType: AirHandlerUnitObjectType.COIL, defectType });

  if (coil.defect) {
    errors.push(getCoilErrorMessage(AirHandlerUnitDefectType.DEFECT));
  }

  if (coil.missmatchDefect) {
    errors.push(getCoilErrorMessage(AirHandlerUnitDefectType.MISS_MATCH_DEFECT));
  }

  if (coil.twoWayValve) {
    errors.push(...getTwoWayValveErrors(coil.twoWayValve));
  }

  if (coil.threeWayValve) {
    errors.push(...getThreeWayValveErrors(coil.threeWayValve));
  }

  if (coil.pump) {
    errors.push(...getPumpErrors(coil.pump));
  }

  return errors;
};

const getCoilsErrors = (coils: Coil[]): string[] => {
  const errors: string[] = [];

  coils.forEach(coil => {
    errors.push(...getCoilErrors(coil));
  });

  return errors;
};

const getHumidifierErrors = (humidifier: Humidifier): string[] => {
  const errors: string[] = [];

  const getHumidifierErrorMessage = (defectType: AirHandlerUnitDefectType) =>
    getMessage({ objectType: AirHandlerUnitObjectType.HUMIDIFIER, defectType });

  if (humidifier.defect) {
    errors.push(getHumidifierErrorMessage(AirHandlerUnitDefectType.DEFECT));
  }

  if (humidifier.missmatchDefect) {
    errors.push(getHumidifierErrorMessage(AirHandlerUnitDefectType.MISS_MATCH_DEFECT));
  }

  return errors;
};

const getDehumidifierErrors = (dehumidifier: Humidifier): string[] => {
  const errors: string[] = [];

  const getDehumidifierErrorMessage = (defectType: AirHandlerUnitDefectType) =>
    getMessage({ objectType: AirHandlerUnitObjectType.DEHUMIDIFIER, defectType });

  if (dehumidifier.defect) {
    errors.push(getDehumidifierErrorMessage(AirHandlerUnitDefectType.DEFECT));
  }

  if (dehumidifier.missmatchDefect) {
    errors.push(getDehumidifierErrorMessage(AirHandlerUnitDefectType.MISS_MATCH_DEFECT));
  }

  return errors;
};

// A duct holds several filters, so the object type follows the (duct, kind) pair.
const FILTER_OBJECT_TYPE: Record<DuctType, Partial<Record<FilterKind, AirHandlerUnitObjectType>>> = {
  [DuctType.OUTSIDE]: {
    [FilterKind.MAIN]: AirHandlerUnitObjectType.OUTSIDE_AIR_FILTER,
    [FilterKind.PRE]: AirHandlerUnitObjectType.OUTSIDE_AIR_PRE_FILTER,
  },
  [DuctType.EXHAUST]: { [FilterKind.MAIN]: AirHandlerUnitObjectType.EXHAUST_AIR_FILTER },
  [DuctType.RETURN]: { [FilterKind.MAIN]: AirHandlerUnitObjectType.RETURN_AIR_FILTER },
  [DuctType.SUPPLY]: {
    [FilterKind.MAIN]: AirHandlerUnitObjectType.SUPPLY_AIR_FILTER,
    [FilterKind.TERMINAL]: AirHandlerUnitObjectType.TERMINAL_AIR_FILTER,
  },
};

const DUCT_OBJECT_TYPE: Record<DuctType, AirHandlerUnitObjectType> = {
  [DuctType.OUTSIDE]: AirHandlerUnitObjectType.OUTSIDE_AIR_DUCT,
  [DuctType.EXHAUST]: AirHandlerUnitObjectType.EXHAUST_AIR_DUCT,
  [DuctType.SUPPLY]: AirHandlerUnitObjectType.SUPPLY_AIR_DUCT,
  [DuctType.RETURN]: AirHandlerUnitObjectType.RETURN_AIR_DUCT,
};

const getFilterErrors = (filter: Filter): string[] => {
  const objectType = FILTER_OBJECT_TYPE[filter.type][filter.kind];

  if (!objectType || !filter.pressureSwitchDefect) return [];

  return [getMessage({ objectType, defectType: AirHandlerUnitDefectType.DEFECT })];
};

const getFiltersErrors = (filters: Filter[]): string[] => filters.flatMap(getFilterErrors);

const FLOW_DEFECTS = [
  ["pressureDefect", AirHandlerUnitDefectType.PRESSURE_DEFECT],
  ["flowDefect", AirHandlerUnitDefectType.FLOW_DEFECT],
] as const satisfies readonly (readonly [keyof Flow, AirHandlerUnitDefectType])[];

const getFlowErrors = (flow: Flow): string[] =>
  FLOW_DEFECTS.filter(([key]) => flow[key]).map(([, defectType]) =>
    getMessage({ objectType: DUCT_OBJECT_TYPE[flow.type], defectType }),
  );

const getFlowsErrors = (flows: Flow[]): string[] => flows.flatMap(getFlowErrors);

/**
 * A probe reports its defect where its reading sits, on the CTA, while the defect names the duct it
 * measures: one defect type per family, the duct carried by the object type.
 */
const SENSOR_DEFECTS = [
  ["outsideAirTemperatureDefect", DuctType.OUTSIDE, AirHandlerUnitDefectType.TEMPERATURE_DEFECT],
  ["exhaustAirTemperatureDefect", DuctType.EXHAUST, AirHandlerUnitDefectType.TEMPERATURE_DEFECT],
  ["supplyAirTemperatureDefect", DuctType.SUPPLY, AirHandlerUnitDefectType.TEMPERATURE_DEFECT],
  ["returnAirTemperatureDefect", DuctType.RETURN, AirHandlerUnitDefectType.TEMPERATURE_DEFECT],
  ["outsideAirCO2Defect", DuctType.OUTSIDE, AirHandlerUnitDefectType.CO2_DEFECT],
  ["exhaustAirCO2Defect", DuctType.EXHAUST, AirHandlerUnitDefectType.CO2_DEFECT],
  ["supplyAirCO2Defect", DuctType.SUPPLY, AirHandlerUnitDefectType.CO2_DEFECT],
  ["returnAirCO2Defect", DuctType.RETURN, AirHandlerUnitDefectType.CO2_DEFECT],
  ["outsideAirHumidityDefect", DuctType.OUTSIDE, AirHandlerUnitDefectType.HUMIDITY_DEFECT],
  ["exhaustAirHumidityDefect", DuctType.EXHAUST, AirHandlerUnitDefectType.HUMIDITY_DEFECT],
  ["supplyAirHumidityDefect", DuctType.SUPPLY, AirHandlerUnitDefectType.HUMIDITY_DEFECT],
  ["returnAirHumidityDefect", DuctType.RETURN, AirHandlerUnitDefectType.HUMIDITY_DEFECT],
] as const satisfies readonly (readonly [keyof FullAirHandlerUnit, DuctType, AirHandlerUnitDefectType])[];

const getSensorErrors = (airHandlerUnit: FullAirHandlerUnit): string[] =>
  SENSOR_DEFECTS.filter(([key]) => airHandlerUnit[key]).map(([, type, defectType]) =>
    getMessage({ objectType: DUCT_OBJECT_TYPE[type], defectType }),
  );

const getAirHandlerUnitErrorMessage = (props: Omit<Parameters<typeof getMessage>[0], "objectType">) =>
  getMessage({ ...props, objectType: AirHandlerUnitObjectType.AIR_HANDLER_UNIT });

const getAirHandlerUnitErrors = (airHandlerUnit: FullAirHandlerUnit): AirHandlerUnitError[] => {
  const errors: AirHandlerUnitError[] = [];

  const airHandlerUnitId = airHandlerUnit.id;
  const airHandlerUnitName = airHandlerUnit.name;

  if (airHandlerUnit.defect) {
    errors.push({
      airHandlerUnitId,
      airHandlerUnitName,
      message: getAirHandlerUnitErrorMessage({ defectType: AirHandlerUnitDefectType.DEFECT }),
    });
  }

  if (airHandlerUnit.airDefect) {
    errors.push({
      airHandlerUnitId,
      airHandlerUnitName,
      message: getAirHandlerUnitErrorMessage({ defectType: AirHandlerUnitDefectType.AIR_DEFECT }),
    });
  }

  if (airHandlerUnit.communicationDefect) {
    errors.push({
      airHandlerUnitId,
      airHandlerUnitName,
      message: getAirHandlerUnitErrorMessage({ defectType: AirHandlerUnitDefectType.COMMUNICATION_DEFECT }),
    });
  }

  if (airHandlerUnit.antiFreezeDefect) {
    errors.push({
      airHandlerUnitId,
      airHandlerUnitName,
      message: getAirHandlerUnitErrorMessage({ defectType: AirHandlerUnitDefectType.ANTIFREEZE_DEFECT }),
    });
  }

  if (airHandlerUnit.autonomousTriggerDetectorDefect) {
    errors.push({
      airHandlerUnitId,
      airHandlerUnitName,
      message: getAirHandlerUnitErrorMessage({
        defectType: AirHandlerUnitDefectType.AUTONOMOUS_TRIGGER_DETECTOR_DEFECT,
      }),
    });
  }

  if (airHandlerUnit.fireDefect) {
    errors.push({
      airHandlerUnitId,
      airHandlerUnitName,
      message: getAirHandlerUnitErrorMessage({ defectType: AirHandlerUnitDefectType.FIRE_DEFECT }),
    });
  }

  if (airHandlerUnit.missmatchDefect) {
    errors.push({
      airHandlerUnitId,
      airHandlerUnitName,
      message: getAirHandlerUnitErrorMessage({ defectType: AirHandlerUnitDefectType.MISS_MATCH_DEFECT }),
    });
  }

  if (airHandlerUnit.smokeDefect) {
    errors.push({
      airHandlerUnitId,
      airHandlerUnitName,
      message: getAirHandlerUnitErrorMessage({ defectType: AirHandlerUnitDefectType.SMOKE_DEFECT }),
    });
  }

  if (airHandlerUnit.airFlowPressureSwitchDefect) {
    errors.push({
      airHandlerUnitId,
      airHandlerUnitName,
      message: getAirHandlerUnitErrorMessage({ defectType: AirHandlerUnitDefectType.PRESSURE_SWITCH_DEFECT }),
    });
  }

  if (airHandlerUnit.filters) {
    const errorsMessages = getFiltersErrors(airHandlerUnit.filters);
    errors.push(...errorsMessages.map(error => ({ airHandlerUnitId, airHandlerUnitName, message: error })));
  }

  if (airHandlerUnit.flows) {
    const errorsMessages = getFlowsErrors(airHandlerUnit.flows);
    errors.push(...errorsMessages.map(error => ({ airHandlerUnitId, airHandlerUnitName, message: error })));
  }

  errors.push(
    ...getSensorErrors(airHandlerUnit).map(error => ({ airHandlerUnitId, airHandlerUnitName, message: error })),
  );

  if (airHandlerUnit.fans) {
    const errorsMessages = getFansErrors(airHandlerUnit.fans);
    errors.push(...errorsMessages.map(error => ({ airHandlerUnitId, airHandlerUnitName, message: error })));
  }

  if (airHandlerUnit.recoveryWheel) {
    const errorsMessages = getRecoveryWheelErrors(airHandlerUnit.recoveryWheel);
    errors.push(...errorsMessages.map(error => ({ airHandlerUnitId, airHandlerUnitName, message: error })));
  }

  if (airHandlerUnit.dampers) {
    const errorsMessages = getDampersErrors(airHandlerUnit.dampers);
    errors.push(...errorsMessages.map(error => ({ airHandlerUnitId, airHandlerUnitName, message: error })));
  }

  if (airHandlerUnit.coils) {
    const errorsMessages = getCoilsErrors(airHandlerUnit.coils);
    errors.push(...errorsMessages.map(error => ({ airHandlerUnitId, airHandlerUnitName, message: error })));
  }

  if (airHandlerUnit.humidifier) {
    const errorsMessages = getHumidifierErrors(airHandlerUnit.humidifier);
    errors.push(...errorsMessages.map(error => ({ airHandlerUnitId, airHandlerUnitName, message: error })));
  }

  if (airHandlerUnit.dehumidifier) {
    const errorsMessages = getDehumidifierErrors(airHandlerUnit.dehumidifier);
    errors.push(...errorsMessages.map(error => ({ airHandlerUnitId, airHandlerUnitName, message: error })));
  }

  return errors;
};

export const getErrors = (airHandlerUnits: FullAirHandlerUnit[]): AirHandlerUnitError[] => {
  const errors: AirHandlerUnitError[] = [];

  airHandlerUnits.forEach(airHandlerUnit => {
    errors.push(...getAirHandlerUnitErrors(airHandlerUnit));
  });

  return errors;
};
